import React, { useState } from 'react';
import MaturityUpload from './MaturityUpload';

/* ═══════════════════════════════════════════════════════════════
   Section 1.148-4 Arbitrage Yield Calculator

   Implements yield calculation per Treas. Reg. 1.148-4 using:
   - 30/360 day count (US Bond Basis)
   - Odd first coupon support
   - Call date assumption per 1.148-4(b)(3): Condition A (5-year
     callable, whole-issue yield spread test) and Condition B
     (per-maturity excess premium test)
   - Lower-yield rule per 1.148-4(b)(2)
   - Bisection solver for issue yield
   ═══════════════════════════════════════════════════════════════ */

// ── 30/360 Day Count (US Bond Basis) ─────────────────────────
function days30360(d1, d2) {
  let y1 = d1.getFullYear(), m1 = d1.getMonth() + 1, d1d = d1.getDate();
  let y2 = d2.getFullYear(), m2 = d2.getMonth() + 1, d2d = d2.getDate();
  if (d1d === 31) d1d = 30;
  if (d2d === 31 && d1d === 30) d2d = 30;
  return 360 * (y2 - y1) + 30 * (m2 - m1) + (d2d - d1d);
}

// ── Date helpers ─────────────────────────────────────────────
function parseDate(s) {
  if (!s) return null;
  const parts = s.split(/[-/]/);
  if (parts.length !== 3) return null;
  let m, d, y;
  if (parts[0].length === 4) { y = +parts[0]; m = +parts[1]; d = +parts[2]; }
  else { m = +parts[0]; d = +parts[1]; y = +parts[2]; }
  return new Date(y, m - 1, d);
}

function fmtDate(dt) {
  if (!dt) return '';
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${dt.getFullYear()}`;
}

function addMonths(dt, n) {
  const r = new Date(dt);
  r.setMonth(r.getMonth() + n);
  return r;
}

function dateLTE(a, b) { return a.getTime() <= b.getTime(); }
function dateEQ(a, b) { return a.getTime() === b.getTime(); }

// ── Frequency constants ─────────────────────────────────────
// periodsPerYear: compounding periods; monthsPerPeriod: coupon interval;
// daysPerPeriod: 30/360 days in one period
const FREQUENCY = {
  2:  { periodsPerYear: 2,  monthsPerPeriod: 6,  daysPerPeriod: 180 },  // Semiannual
  1:  { periodsPerYear: 1,  monthsPerPeriod: 12, daysPerPeriod: 360 },  // Annual
  12: { periodsPerYear: 12, monthsPerPeriod: 1,  daysPerPeriod: 30 },   // Monthly
};

// ── Generate coupon dates from firstCouponDate ──────────────
function generateCouponDates(firstCoupon, maturity, monthsPerPeriod = 6) {
  const dates = [firstCoupon];
  let d = addMonths(firstCoupon, monthsPerPeriod);
  while (dateLTE(d, maturity)) {
    dates.push(new Date(d));
    d = addMonths(d, monthsPerPeriod);
  }
  return dates;
}

// ── Build cash flows for one maturity ────────────────────────
function buildCashFlows(issueDate, firstCoupon, matDate, par, coupon, redemptionDate, redemptionAmt, freq = FREQUENCY[2]) {
  const flows = [];
  const n = freq.periodsPerYear;
  const dpp = freq.daysPerPeriod;
  const periodicCoupon = par * (coupon / 100) / n;
  const couponDates = generateCouponDates(firstCoupon, redemptionDate, freq.monthsPerPeriod);

  for (let i = 0; i < couponDates.length; i++) {
    const cd = couponDates[i];
    if (cd.getTime() > redemptionDate.getTime()) break;
    let interest;
    if (i === 0) {
      const stubDays = days30360(issueDate, firstCoupon);
      interest = par * (coupon / 100 / n) * (stubDays / dpp);
    } else {
      interest = periodicCoupon;
    }
    let principal = 0;
    if (dateEQ(cd, redemptionDate)) {
      principal = redemptionAmt;
    }
    if (interest > 0 || principal > 0) {
      flows.push({ date: cd, interest, principal, total: interest + principal });
    }
  }
  // If redemption date doesn't fall on a coupon date
  if (!couponDates.some(cd => dateEQ(cd, redemptionDate))) {
    flows.push({ date: redemptionDate, interest: 0, principal: redemptionAmt, total: redemptionAmt });
  }
  return flows;
}

// ── Solve yield for a single maturity (price -> yield) ───────
function solveYieldSingle(issueDate, firstCoupon, matDate, par, coupon, price, redemptionDate, redemptionAmt, freq = FREQUENCY[2]) {
  const flows = buildCashFlows(issueDate, firstCoupon, matDate, par, coupon, redemptionDate, redemptionAmt, freq);
  if (flows.length === 0) return { yield: 0, flows };
  const n = freq.periodsPerYear;
  const dpp = freq.daysPerPeriod;

  function pv(yld) {
    let total = 0;
    for (const f of flows) {
      const d = days30360(issueDate, f.date);
      const factor = Math.pow(1 + yld / n, -(d / dpp));
      total += f.total * factor;
    }
    return total;
  }

  let lo = -0.05, hi = 0.50;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const pvMid = pv(mid);
    if (Math.abs(pvMid - price) < 0.001) return { yield: mid, flows };
    if (pvMid > price) lo = mid; else hi = mid;
  }
  return { yield: (lo + hi) / 2, flows };
}

// ── §1.148-4(b)(3) call date assumption tests ───────────────
//
// Condition A — callable within 5 years (whole-issue test):
//   If call is within 5 years of issue AND issueYield(all-to-mat) minus
//   issueYield(all-to-call) exceeds 1/8 of 1% (0.00125), ALL callable
//   maturities use the call date.
//
// Condition B — excess premium (per-maturity test):
//   completeYearsToCall = floor(days30360(issueDate, callDate) / 360)
//   threshold = par × 0.0025 × completeYearsToCall
//   If premium > threshold AND maturity is after call → use call date.
//
// When a condition triggers, §1.148-4(b)(2) still applies: use whichever
// of yield-to-maturity or yield-to-call produces the LOWER yield.

function testConditionA(issueDate, firstCoupon, parsedMats, callDt, totalIssuePrice, freq = FREQUENCY[2]) {
  // Only applies if call is within 5 years of issue
  const yearsToCall = days30360(issueDate, callDt) / 360;
  if (yearsToCall > 5) return false;

  // Identify maturities subject to early redemption (after call date)
  const callableMats = parsedMats.filter(m => m.matDate.getTime() > callDt.getTime());
  if (callableMats.length === 0) return false;

  // Build two sets of flows: all-to-maturity vs all-to-call
  let flowsToMat = [];
  let flowsToCall = [];
  for (const m of parsedMats) {
    const isCallable = m.matDate.getTime() > callDt.getTime();
    const matFlows = buildCashFlows(issueDate, firstCoupon, m.matDate, m.par, m.coupon, m.matDate, m.par, freq);
    flowsToMat = flowsToMat.concat(matFlows);
    if (isCallable) {
      const callFlows = buildCashFlows(issueDate, firstCoupon, m.matDate, m.par, m.coupon, callDt, m.par, freq);
      flowsToCall = flowsToCall.concat(callFlows);
    } else {
      flowsToCall = flowsToCall.concat(matFlows);
    }
  }

  const yieldToMat = solveIssueYield(issueDate, flowsToMat, totalIssuePrice, freq).yield;
  const yieldToCall = solveIssueYield(issueDate, flowsToCall, totalIssuePrice, freq).yield;

  return (yieldToMat - yieldToCall) > 0.00125;
}

function testConditionB(issueDate, mat, callDt) {
  // Per-maturity: only if maturity is after call and there is a premium
  if (mat.matDate.getTime() <= callDt.getTime()) return { triggered: false };
  const premium = mat.issuePrice - mat.par;
  if (premium <= 0) return { triggered: false };

  const completeYearsToCall = Math.floor(days30360(issueDate, callDt) / 360);
  const threshold = mat.par * 0.0025 * completeYearsToCall;

  return { triggered: premium > threshold, premium, threshold, completeYearsToCall };
}

function resolveCallOrMaturity(issueDate, firstCoupon, mat, callDt, freq = FREQUENCY[2]) {
  // §1.148-4(b)(2): use whichever produces the LOWER yield
  const toMat = solveYieldSingle(issueDate, firstCoupon, mat.matDate, mat.par, mat.coupon, mat.issuePrice, mat.matDate, mat.par, freq);
  const toCall = solveYieldSingle(issueDate, firstCoupon, mat.matDate, mat.par, mat.coupon, mat.issuePrice, callDt, mat.par, freq);
  if (toCall.yield < toMat.yield) {
    return { useCall: true, yieldToMat: toMat.yield, yieldToCall: toCall.yield, flows: toCall.flows };
  }
  return { useCall: false, yieldToMat: toMat.yield, yieldToCall: toCall.yield, flows: toMat.flows };
}

// ── Merge cash flows and solve issue yield ───────────────────
function solveIssueYield(issueDate, allFlows, totalIssuePrice, freq = FREQUENCY[2]) {
  const n = freq.periodsPerYear;
  const dpp = freq.daysPerPeriod;
  // Merge by date
  const merged = {};
  for (const f of allFlows) {
    const key = f.date.getTime();
    if (!merged[key]) merged[key] = { date: f.date, principal: 0, interest: 0, total: 0 };
    merged[key].principal += f.principal;
    merged[key].interest += f.interest;
    merged[key].total += f.total;
  }
  const sortedFlows = Object.values(merged).sort((a, b) => a.date - b.date);

  function pv(yld) {
    let total = 0;
    for (const f of sortedFlows) {
      const d = days30360(issueDate, f.date);
      const factor = Math.pow(1 + yld / n, -(d / dpp));
      total += f.total * factor;
    }
    return total;
  }

  let lo = -0.02, hi = 0.30;
  for (let i = 0; i < 300; i++) {
    const mid = (lo + hi) / 2;
    const pvMid = pv(mid);
    if (Math.abs(pvMid - totalIssuePrice) < 0.001) return { yield: mid, flows: sortedFlows };
    if (pvMid > totalIssuePrice) lo = mid; else hi = mid;
  }
  return { yield: (lo + hi) / 2, flows: sortedFlows };
}

// ── Styling helpers ──────────────────────────────────────────
const sHdr = { background: 'var(--ribbon-bg)', color: 'var(--ribbon-text)', padding: '6px 14px',
  fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase',
  borderRadius: '4px', marginBottom: '12px', marginTop: '4px' };

const sInput = { width: '100%', padding: '7px 10px', border: '1px solid var(--border-color)',
  borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)' };

const sLabel = { display: 'block', fontSize: '11px', fontWeight: '600',
  color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' };

const sBtn = { padding: '8px 18px', borderRadius: '4px', border: 'none', cursor: 'pointer',
  fontSize: '13px', fontWeight: '600', fontFamily: 'var(--font-body)', transition: 'var(--transition-fast)' };

const sBtnPrimary = { ...sBtn, background: 'var(--accent-color)', color: '#fff' };
const sBtnSecondary = { ...sBtn, background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' };

const sTable = { width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'var(--font-body)' };
const sTh = { padding: '6px 8px', textAlign: 'right', fontWeight: '600', fontSize: '11px',
  textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-secondary)',
  borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)' };
const sTd = { padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid var(--border-light)',
  fontFamily: "'DM Sans', monospace", fontSize: '12px' };

const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
const fmtPct = (v, dec = 6) => v != null ? (v * 100).toFixed(dec) + '%' : '';

// ── Component ────────────────────────────────────────────────
const YieldCalculator = () => {
  const [issueDate, setIssueDate] = useState('');
  const [firstCoupon, setFirstCoupon] = useState('');
  const [firstPrincipalDate, setFirstPrincipalDate] = useState('');
  const [totalIssuePrice, setTotalIssuePrice] = useState('');
  const [principalFrequency, setPrincipalFrequency] = useState(2); // for schedule generation
  const [interestFrequency, setInterestFrequency] = useState(2);  // for yield math (compounding)
  const [finalMaturityDate, setFinalMaturityDate] = useState('');
  const [hasCall, setHasCall] = useState(false);
  const [callDate, setCallDate] = useState('');
  const [maturities, setMaturities] = useState([
    { maturityDate: '', par: '', coupon: '', issuePrice: '' }
  ]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const uploadColumns = [
    { key: 'maturity_date', label: 'Maturity Date' },
    { key: 'par_amount', label: 'Par Amount' },
    { key: 'coupon', label: 'Coupon (%)' },
    { key: 'price', label: 'Price ($)' },
    { key: 'call_date', label: 'Call Date' },
  ];

  const handlePopulate = (parsed) => {
    const mapped = parsed
      .filter(r => r.maturity_date || r.par_amount)
      .map(r => ({
        maturityDate: r.maturity_date || '',
        par: r.par_amount != null ? String(r.par_amount) : '',
        coupon: r.coupon != null ? String(r.coupon) : '',
        issuePrice: r.price != null ? String(r.price) : '',
      }));
    if (mapped.length > 0) {
      setMaturities(mapped);
      // If all rows share the same call date, auto-fill it
      const callDates = parsed.map(r => r.call_date).filter(Boolean);
      const uniqueCalls = [...new Set(callDates)];
      if (uniqueCalls.length === 1) {
        setHasCall(true);
        setCallDate(uniqueCalls[0]);
      }
    }
  };

  const addMaturity = () => {
    setMaturities(prev => [...prev, { maturityDate: '', par: '', coupon: '', issuePrice: '' }]);
  };

  const removeMaturity = (idx) => {
    setMaturities(prev => prev.filter((_, i) => i !== idx));
  };

  const updateMaturity = (idx, field, value) => {
    setMaturities(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const generateSchedule = () => {
    setError('');
    try {
      const firstCouponDt = parseDate(firstCoupon);
      const firstPrincipalDt = parseDate(firstPrincipalDate);
      const finalDt = parseDate(finalMaturityDate);
      if (!firstCouponDt && !firstPrincipalDt) throw new Error('Enter a First Coupon Date or First Principal Payment Date before generating');
      if (!finalDt) throw new Error('Enter a Final Maturity Date before generating');
      const startDt = firstPrincipalDt || firstCouponDt;
      if (finalDt.getTime() < startDt.getTime()) throw new Error('Final Maturity Date must be on or after the first principal payment date');

      const monthsPerPeriod = FREQUENCY[principalFrequency].monthsPerPeriod;
      const rows = [];
      // First principal payment starts from First Principal Payment Date if provided, else First Coupon Date
      let d = new Date(startDt);
      while (dateLTE(d, finalDt)) {
        rows.push({ maturityDate: fmtDate(d), par: '', coupon: '', issuePrice: '' });
        d = addMonths(d, monthsPerPeriod);
      }
      // Ensure final maturity is included even if it doesn't land on a regular period
      if (rows.length === 0 || rows[rows.length - 1].maturityDate !== fmtDate(finalDt)) {
        rows.push({ maturityDate: fmtDate(finalDt), par: '', coupon: '', issuePrice: '' });
      }
      setMaturities(rows);
    } catch (e) {
      setError(e.message);
    }
  };

  const calculate = () => {
    setError('');
    setResult(null);

    try {
      const freq = FREQUENCY[interestFrequency];
      const issueDt = parseDate(issueDate);
      const firstCouponDt = parseDate(firstCoupon);
      if (!issueDt || !firstCouponDt) throw new Error('Invalid issue date or first coupon date');

      const tip = parseFloat(totalIssuePrice.replace(/,/g, ''));
      if (isNaN(tip) || tip <= 0) throw new Error('Invalid total issue price');

      const stubDays = days30360(issueDt, firstCouponDt);
      const stubLabel = stubDays === freq.daysPerPeriod ? 'exact' : stubDays < freq.daysPerPeriod ? 'short' : 'long';

      // Parse all maturities up front
      const parsedMats = maturities.map((m, i) => {
        const matDate = parseDate(m.maturityDate);
        if (!matDate) throw new Error(`Maturity ${i + 1}: invalid date`);
        const par = parseFloat(String(m.par).replace(/,/g, ''));
        const coupon = parseFloat(m.coupon);
        const ip = parseFloat(String(m.issuePrice).replace(/,/g, ''));
        if (isNaN(par) || isNaN(coupon) || isNaN(ip)) throw new Error(`Maturity ${i + 1}: invalid numbers`);
        return { maturityDate: m.maturityDate, matDate, par, coupon, issuePrice: ip };
      });

      const callDt = (hasCall && callDate) ? parseDate(callDate) : null;
      if (hasCall && !callDt) throw new Error('Call provisions enabled but call date is invalid');

      // §1.148-4(b)(3) — determine which maturities use call date
      let conditionA = false;
      if (callDt) {
        conditionA = testConditionA(issueDt, firstCouponDt, parsedMats, callDt, tip, freq);
      }

      const matResults = [];
      let allFlows = [];

      for (const mat of parsedMats) {
        const isAfterCall = callDt && mat.matDate.getTime() > callDt.getTime();
        let applied = 'maturity';
        let condition = '';
        let yieldToMat = null;
        let yieldToCall = null;
        let condBThreshold = null;
        let condBPremium = null;
        let flows;

        if (callDt && isAfterCall) {
          // Check Condition A (whole-issue, already computed)
          const condB = testConditionB(issueDt, mat, callDt);
          const triggered = conditionA || condB.triggered;

          if (triggered) {
            const resolution = resolveCallOrMaturity(issueDt, firstCouponDt, mat, callDt, freq);
            yieldToMat = resolution.yieldToMat;
            yieldToCall = resolution.yieldToCall;
            flows = resolution.flows;
            applied = resolution.useCall ? 'call' : 'maturity';
            condition = conditionA ? 'A' : 'B';
            if (condB.triggered) {
              condBThreshold = condB.threshold;
              condBPremium = condB.premium;
              // If both A and B, note both
              if (conditionA) condition = 'A+B';
              else condition = 'B';
            }
          } else {
            // No condition triggered — use maturity
            const matResult = solveYieldSingle(issueDt, firstCouponDt, mat.matDate, mat.par, mat.coupon, mat.issuePrice, mat.matDate, mat.par, freq);
            yieldToMat = matResult.yield;
            flows = matResult.flows;
            applied = 'maturity';
            condition = 'none';
            if (condB.threshold != null) {
              condBThreshold = condB.threshold;
              condBPremium = condB.premium;
            }
          }
        } else {
          // At or before call, or no call — always use maturity
          const matResult = solveYieldSingle(issueDt, firstCouponDt, mat.matDate, mat.par, mat.coupon, mat.issuePrice, mat.matDate, mat.par, freq);
          yieldToMat = matResult.yield;
          flows = matResult.flows;
          applied = 'maturity';
          condition = callDt ? (isAfterCall ? 'none' : '') : '';
        }

        matResults.push({
          maturityDate: mat.maturityDate,
          par: mat.par,
          coupon: String(mat.coupon),
          issuePrice: mat.issuePrice,
          premium: mat.issuePrice - mat.par,
          applied,
          condition,
          yieldToMat,
          yieldToCall,
          condBThreshold,
          condBPremium,
        });

        allFlows = allFlows.concat(flows);
      }

      // Solve issue yield
      const { yield: issueYield, flows: mergedFlows } = solveIssueYield(issueDt, allFlows, tip, freq);

      // Build debt service table with discount factors
      const debtServiceTable = mergedFlows.map(f => {
        const d = days30360(issueDt, f.date);
        const factor = Math.pow(1 + issueYield / freq.periodsPerYear, -(d / freq.daysPerPeriod));
        return {
          date: f.date,
          principal: f.principal,
          interest: f.interest,
          total: f.total,
          days: d,
          factor,
          pv: f.total * factor,
        };
      });

      const totalPar = matResults.reduce((s, m) => s + m.par, 0);
      const totalPrincipal = debtServiceTable.reduce((s, r) => s + r.principal, 0);
      const totalInterest = debtServiceTable.reduce((s, r) => s + r.interest, 0);
      const totalPayments = debtServiceTable.reduce((s, r) => s + r.total, 0);
      const totalPV = debtServiceTable.reduce((s, r) => s + r.pv, 0);

      setResult({
        issueYield,
        stubDays,
        stubLabel,
        matResults,
        debtServiceTable,
        totalPar,
        totalPrincipal,
        totalInterest,
        totalPayments,
        totalPV,
        totalIssuePrice: tip,
        conditionA,
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const exportCSV = () => {
    if (!result) return;
    let csv = 'Payment Date,Principal,Interest,Total Payment,Days (30/360),Discount Factor,Present Value\n';
    for (const r of result.debtServiceTable) {
      csv += `${fmtDate(r.date)},${r.principal.toFixed(2)},${r.interest.toFixed(2)},${r.total.toFixed(2)},${r.days},${r.factor.toFixed(8)},${r.pv.toFixed(2)}\n`;
    }
    csv += `TOTALS,${result.totalPrincipal.toFixed(2)},${result.totalInterest.toFixed(2)},${result.totalPayments.toFixed(2)},,,"${result.totalPV.toFixed(2)}"\n`;
    csv += `\nIssue Yield:,${fmtPct(result.issueYield)}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'yield_calculation.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportReport = () => {
    if (!result) return;
    let rpt = `SECTION 1.148-4 ARBITRAGE YIELD CALCULATION\n${'='.repeat(50)}\n\n`;
    rpt += `Issue Date: ${issueDate}\nFirst Coupon Date: ${firstCoupon}\n`;
    rpt += `Stub Period: ${result.stubDays} days (${result.stubLabel})\n`;
    rpt += `Total Issue Price: ${fmt$(result.totalIssuePrice)}\n`;
    if (hasCall) rpt += `Call Date: ${callDate} (at par)\n`;
    rpt += `\nCOMPUTED ISSUE YIELD: ${fmtPct(result.issueYield)}\n\n`;
    rpt += `MATURITY SCHEDULE\n${'-'.repeat(50)}\n`;
    for (const m of result.matResults) {
      rpt += `${m.maturityDate}  Par: ${fmt$(m.par)}  Coupon: ${m.coupon}%  Price: ${fmt$(m.issuePrice)}  Premium: ${fmt$(m.premium)}`;
      if (m.yieldToCall !== null) rpt += `  YTM: ${fmtPct(m.yieldToMat, 4)}  YTC: ${fmtPct(m.yieldToCall, 4)}  Applied: ${m.applied}`;
      rpt += '\n';
    }
    rpt += `\nAGGREGATE DEBT SERVICE\n${'-'.repeat(50)}\n`;
    rpt += 'Date            Principal        Interest         Total     Days    Discount Factor   Present Value\n';
    for (const r of result.debtServiceTable) {
      rpt += `${fmtDate(r.date).padEnd(16)}${fmt$(r.principal).padStart(16)}${fmt$(r.interest).padStart(16)}${fmt$(r.total).padStart(14)}${String(r.days).padStart(8)}${r.factor.toFixed(8).padStart(19)}${fmt$(r.pv).padStart(16)}\n`;
    }
    rpt += `${'TOTALS'.padEnd(16)}${fmt$(result.totalPrincipal).padStart(16)}${fmt$(result.totalInterest).padStart(16)}${fmt$(result.totalPayments).padStart(14)}${''.padStart(8)}${''.padStart(19)}${fmt$(result.totalPV).padStart(16)}\n`;
    const blob = new Blob([rpt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'yield_report.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', fontFamily: 'var(--font-body)',
      maxWidth: '1000px', margin: '0 auto', width: '100%' }}>

      <h2 style={{ fontFamily: 'var(--font-reading)', fontSize: '22px', fontWeight: '600',
        color: 'var(--text-primary)', marginBottom: '4px' }}>
        Arbitrage Yield Calculator
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
        Computes issue yield per Treas. Reg. 1.148-4 using 30/360 day count with odd first coupon support
      </p>

      {/* ── File Upload ─────────────────────────────────── */}
      <MaturityUpload columns={uploadColumns} onPopulate={handlePopulate} />

      {/* ── Bond Parameters ─────────────────────────────── */}
      <div style={sHdr}>Bond Parameters</div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label style={sLabel}>Delivery Date</label>
          <input type="text" value={issueDate} onChange={e => setIssueDate(e.target.value)}
            placeholder="MM/DD/YYYY" style={sInput} />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={sLabel}>First Coupon Date</label>
          <input type="text" value={firstCoupon} onChange={e => setFirstCoupon(e.target.value)}
            placeholder="MM/DD/YYYY" style={sInput} />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={sLabel}>First Principal Payment Date</label>
          <input type="text" value={firstPrincipalDate} onChange={e => setFirstPrincipalDate(e.target.value)}
            placeholder="MM/DD/YYYY" style={sInput} />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={sLabel}>Final Maturity Date</label>
          <input type="text" value={finalMaturityDate} onChange={e => setFinalMaturityDate(e.target.value)}
            placeholder="MM/DD/YYYY" style={sInput} />
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <label style={sLabel}>Total Issue Price ($)</label>
          <input type="text" value={totalIssuePrice} onChange={e => setTotalIssuePrice(e.target.value)}
            placeholder="e.g. 25297777.40" style={sInput} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ flex: '0 0 180px' }}>
          <label style={sLabel}>Principal Pmt Frequency</label>
          <select value={principalFrequency} onChange={e => setPrincipalFrequency(Number(e.target.value))}
            style={{ ...sInput, cursor: 'pointer' }}>
            <option value={1}>Annual</option>
            <option value={2}>Semiannual</option>
            <option value={12}>Monthly</option>
          </select>
        </div>
        <div style={{ flex: '0 0 180px' }}>
          <label style={sLabel}>Interest Pmt Frequency</label>
          <select value={interestFrequency} onChange={e => setInterestFrequency(Number(e.target.value))}
            style={{ ...sInput, cursor: 'pointer' }}>
            <option value={1}>Annual</option>
            <option value={2}>Semiannual</option>
            <option value={12}>Monthly</option>
          </select>
        </div>
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end' }}>
          <button onClick={generateSchedule} style={sBtnSecondary}>Generate Schedule</button>
        </div>
      </div>

      {/* Call provisions */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
          fontSize: '13px', color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={hasCall} onChange={e => setHasCall(e.target.checked)}
            style={{ accentColor: 'var(--accent-color)' }} />
          <span>Call provisions apply (call price at par)</span>
        </label>
        {hasCall && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ maxWidth: '200px' }}>
              <label style={sLabel}>Call Date</label>
              <input type="text" value={callDate} onChange={e => setCallDate(e.target.value)}
                placeholder="MM/DD/YYYY" style={sInput} />
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '6px', fontStyle: 'italic', maxWidth: '500px' }}>
              Tests conditions A and B per Treas. Reg. 1.148-4(b)(3). Stepped coupon bonds (condition C) not supported.
            </div>
          </div>
        )}
      </div>

      {/* ── Maturity Table ──────────────────────────────── */}
      <div style={sHdr}>Maturity Schedule</div>

      <table style={sTable}>
        <thead>
          <tr>
            <th style={{ ...sTh, textAlign: 'left' }}>Maturity Date</th>
            <th style={sTh}>Par Amount</th>
            <th style={sTh}>Coupon (%)</th>
            <th style={sTh}>Issue Price ($)</th>
            <th style={{ ...sTh, width: '40px' }}></th>
          </tr>
        </thead>
        <tbody>
          {maturities.map((m, i) => (
            <tr key={i}>
              <td style={{ ...sTd, textAlign: 'left' }}>
                <input type="text" value={m.maturityDate} onChange={e => updateMaturity(i, 'maturityDate', e.target.value)}
                  placeholder="MM/DD/YYYY" style={{ ...sInput, width: '120px', padding: '4px 6px', fontSize: '12px' }} />
              </td>
              <td style={sTd}>
                <input type="text" value={m.par} onChange={e => updateMaturity(i, 'par', e.target.value)}
                  placeholder="0" style={{ ...sInput, width: '110px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
              </td>
              <td style={sTd}>
                <input type="text" value={m.coupon} onChange={e => updateMaturity(i, 'coupon', e.target.value)}
                  placeholder="0.000" style={{ ...sInput, width: '80px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
              </td>
              <td style={sTd}>
                <input type="text" value={m.issuePrice} onChange={e => updateMaturity(i, 'issuePrice', e.target.value)}
                  placeholder="0.00" style={{ ...sInput, width: '130px', padding: '4px 6px', fontSize: '12px', textAlign: 'right' }} />
              </td>
              <td style={{ ...sTd, textAlign: 'center' }}>
                {maturities.length > 1 && (
                  <button onClick={() => removeMaturity(i)} title="Remove"
                    style={{ background: 'none', border: 'none', color: 'var(--error-color)',
                      cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}>
                    x
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: '12px', marginTop: '12px', marginBottom: '24px' }}>
        <button onClick={addMaturity} style={sBtnSecondary}>+ Add Maturity</button>
        <button onClick={calculate} style={sBtnPrimary}>Calculate Yield</button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* ── Results ─────────────────────────────────────── */}
      {result && (
        <>
          {/* Yield display */}
          <div style={{ textAlign: 'center', padding: '24px', background: 'var(--bg-secondary)',
            borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginBottom: '8px' }}>
              Computed Issue Yield
            </div>
            <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--accent-color)',
              fontFamily: 'var(--font-reading)', letterSpacing: '-0.02em' }}>
              {fmtPct(result.issueYield)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '6px' }}>
              Stub period: {result.stubDays} days ({result.stubLabel})
              {' | '}PV of debt service: {fmt$(result.totalPV)}
              {' | '}Difference: {fmt$(result.totalPV - result.totalIssuePrice)}
            </div>
          </div>

          {/* Export buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button onClick={exportCSV} style={sBtnSecondary}>Export CSV</button>
            <button onClick={exportReport} style={sBtnSecondary}>Export Report</button>
          </div>

          {/* Maturity-by-maturity table */}
          <div style={sHdr}>Maturity Analysis</div>
          {hasCall && result.conditionA && (
            <div style={{ padding: '8px 12px', background: 'var(--accent-light)', border: '1px solid var(--accent-color)',
              borderRadius: '4px', fontSize: '12px', color: 'var(--accent-color)', marginBottom: '12px' }}>
              Condition A triggered: call within 5 years and issue-level yield spread exceeds 1/8 of 1%.
              All callable maturities subject to call date assumption.
            </div>
          )}
          <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
            <table style={sTable}>
              <thead>
                <tr>
                  <th style={{ ...sTh, textAlign: 'left' }}>Maturity</th>
                  <th style={sTh}>Par</th>
                  <th style={sTh}>Coupon</th>
                  <th style={sTh}>Issue Price</th>
                  <th style={sTh}>Premium</th>
                  {hasCall && <th style={sTh}>YTM</th>}
                  {hasCall && <th style={sTh}>YTC</th>}
                  <th style={sTh}>Applied</th>
                  {hasCall && <th style={sTh}>Condition</th>}
                </tr>
              </thead>
              <tbody>
                {result.matResults.map((m, i) => (
                  <tr key={i}>
                    <td style={{ ...sTd, textAlign: 'left', fontWeight: '500' }}>{m.maturityDate}</td>
                    <td style={sTd}>{fmt$(m.par)}</td>
                    <td style={sTd}>{m.coupon}%</td>
                    <td style={sTd}>{fmt$(m.issuePrice)}</td>
                    <td style={{ ...sTd, color: m.premium > 0 ? 'var(--warning-color)' : 'var(--text-primary)' }}>
                      {fmt$(m.premium)}
                    </td>
                    {hasCall && <td style={sTd}>{m.yieldToMat != null ? fmtPct(m.yieldToMat, 4) : '-'}</td>}
                    {hasCall && <td style={sTd}>{m.yieldToCall != null ? fmtPct(m.yieldToCall, 4) : '-'}</td>}
                    <td style={sTd}>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                        background: m.applied === 'call' ? 'var(--accent-light)' : 'var(--bg-tertiary)',
                        color: m.applied === 'call' ? 'var(--accent-color)' : 'var(--text-secondary)' }}>
                        {m.applied}
                      </span>
                    </td>
                    {hasCall && (
                      <td style={{ ...sTd, fontSize: '11px' }}>
                        {m.condition === 'A' && <span title="Callable within 5 years, yield spread > 1/8%">A</span>}
                        {m.condition === 'B' && (
                          <span title={`Premium ${fmt$(m.condBPremium)} > threshold ${fmt$(m.condBThreshold)}`}>
                            B <span style={{ color: 'var(--text-tertiary)' }}>({fmt$(m.condBPremium)} &gt; {fmt$(m.condBThreshold)})</span>
                          </span>
                        )}
                        {m.condition === 'A+B' && (
                          <span title={`Both conditions met. Premium ${fmt$(m.condBPremium)} > threshold ${fmt$(m.condBThreshold)}`}>
                            A+B
                          </span>
                        )}
                        {m.condition === 'none' && (
                          <span style={{ color: 'var(--text-tertiary)' }}>
                            {m.condBThreshold != null ? `${fmt$(m.condBPremium || 0)} \u2264 ${fmt$(m.condBThreshold)}` : '-'}
                          </span>
                        )}
                        {m.condition === '' && <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Aggregate debt service table */}
          <div style={sHdr}>Aggregate Debt Service</div>
          <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
            <table style={sTable}>
              <thead>
                <tr>
                  <th style={{ ...sTh, textAlign: 'left' }}>Payment Date</th>
                  <th style={sTh}>Principal</th>
                  <th style={sTh}>Interest</th>
                  <th style={sTh}>Total Payment</th>
                  <th style={sTh}>Days (30/360)</th>
                  <th style={sTh}>Discount Factor</th>
                  <th style={sTh}>Present Value</th>
                </tr>
              </thead>
              <tbody>
                {result.debtServiceTable.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...sTd, textAlign: 'left' }}>{fmtDate(r.date)}</td>
                    <td style={sTd}>{fmt$(r.principal)}</td>
                    <td style={sTd}>{fmt$(r.interest)}</td>
                    <td style={{ ...sTd, fontWeight: '500' }}>{fmt$(r.total)}</td>
                    <td style={sTd}>{r.days}</td>
                    <td style={{ ...sTd, fontFamily: 'monospace', fontSize: '11px' }}>{r.factor.toFixed(8)}</td>
                    <td style={sTd}>{fmt$(r.pv)}</td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={{ fontWeight: '700', borderTop: '2px solid var(--border-color)' }}>
                  <td style={{ ...sTd, textAlign: 'left' }}>TOTALS</td>
                  <td style={sTd}>{fmt$(result.totalPrincipal)}</td>
                  <td style={sTd}>{fmt$(result.totalInterest)}</td>
                  <td style={sTd}>{fmt$(result.totalPayments)}</td>
                  <td style={sTd}></td>
                  <td style={sTd}></td>
                  <td style={sTd}>{fmt$(result.totalPV)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default YieldCalculator;
